import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../security/index.js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

/**
 * ProtectedRoute component that ensures only authenticated users can access certain routes
 * Redirects unauthenticated users to login page
 */
const ProtectedRoute = ({
  children,
  fallback = null,
  redirectTo = "/login",
  showLoading = true,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Show loading state while checking authentication
  if (isLoading) {
    if (!showLoading) return fallback;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "background.default",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {t("auth.verifyingSession", "Verifying your session...")}
        </Typography>
      </Box>
    );
  }

  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={redirectTo}
        state={{
          from: location.pathname,
          message: t("auth.loginRequired", "Please log in to access this page"),
        }}
        replace
      />
    );
  }

  // If authenticated, render the protected content
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  redirectTo: PropTypes.string,
  showLoading: PropTypes.bool,
};

export default ProtectedRoute;
